import { useEffect, useState } from "react";
import { X, Send, Loader2, Bell, Mail, MessageSquare, Users, MapPin } from "lucide-react";
import { MapContainer, TileLayer, Polygon, CircleMarker, useMapEvents } from "react-leaflet";
import { toast } from "sonner";
import api from "@/lib/api";

const box = "w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-100";

const TEMPLATES = [
  { label: "— Szablon —", kind: "", title: "", body: "" },
  { label: "Awaria zasilania", kind: "awaria", title: "Przerwa w dostępie – awaria zasilania", body: "Wystąpiła awaria zasilania w Państwa rejonie. Trwają prace nad przywróceniem usługi. Przepraszamy za utrudnienia." },
  { label: "Awaria światłowodu", kind: "awaria", title: "Awaria sieci światłowodowej", body: "Zidentyfikowaliśmy uszkodzenie sieci światłowodowej w Państwa okolicy. Nasi technicy pracują nad usunięciem usterki." },
  { label: "Prace planowe – modernizacja", kind: "prace", title: "Zaplanowane prace serwisowe", body: "Informujemy o planowanych pracach modernizacyjnych. W podanym oknie czasowym mogą wystąpić przerwy w dostępie do usług." },
  { label: "Prace planowe – konserwacja", kind: "prace", title: "Konserwacja sieci", body: "Przeprowadzamy planową konserwację sieci. Prosimy o wyrozumiałość – usługa może być chwilowo niedostępna." },
];

function fillPlaceholders(text, c, windowStr) {
  if (!text) return text;
  const repl = { "{imie}": c.name || "", "{nazwa}": c.name || "", "{miasto}": c.city || "", "{ulica}": c.street || "", "{adres}": c.address || "", "{okno_czasowe}": windowStr || "" };
  return Object.entries(repl).reduce((s, [k, v]) => s.split(k).join(v), text);
}

function PolyPicker({ polygon, setPolygon }) {
  useMapEvents({ click(e) { setPolygon([...polygon, [e.latlng.lat, e.latlng.lng]]); } });
  return (<>
    {polygon.length >= 3 && <Polygon positions={polygon} pathOptions={{ color: "#f59e0b", fillOpacity: 0.15 }} />}
    {polygon.map((p, i) => <CircleMarker key={i} center={p} radius={4} pathOptions={{ color: "#f59e0b", fillColor: "#f59e0b", fillOpacity: 1 }} />)}
  </>);
}

export default function FaultBroadcastDialog({ onClose, initial = null }) {
  const [targets, setTargets] = useState({ cities: [], streets: [], closures: [] });
  const [cities, setCities] = useState([]);
  const [streets, setStreets] = useState([]);
  const [closureIds, setClosureIds] = useState(initial?.closureIds || []);
  const [splitterIds] = useState(initial?.splitterIds || []);
  const [polygon, setPolygon] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [customTpls, setCustomTpls] = useState([]);
  const [points, setPoints] = useState([]);
  const [previewSample, setPreviewSample] = useState(null);
  const [channels, setChannels] = useState({ push: true, email: false, sms: false });
  const [kind, setKind] = useState(initial?.kind || "awaria");
  const [title, setTitle] = useState(initial?.title || "");
  const [body, setBody] = useState("");
  const [wf, setWf] = useState("");
  const [wt, setWt] = useState("");
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clientIds, setClientIds] = useState([]);
  const [clientSel, setClientSel] = useState([]);
  const [cq, setCq] = useState("");
  const [cResults, setCResults] = useState([]);
  const searchClients = async (v) => {
    setCq(v);
    if ((v || "").trim().length < 2) { setCResults([]); return; }
    try { const { data } = await api.get("/faults/broadcast/clients", { params: { q: v } }); setCResults(data.clients || []); }
    catch { setCResults([]); }
  };
  const addClient = (c) => {
    if (clientIds.includes(c.id)) return;
    setClientIds((p) => [...p, c.id]); setClientSel((p) => [...p, c]); setCq(""); setCResults([]);
  };
  const removeClient = (id) => { setClientIds((p) => p.filter((x) => x !== id)); setClientSel((p) => p.filter((c) => c.id !== id)); };

  useEffect(() => {
    api.get("/faults/broadcast/targets").then((r) => setTargets(r.data)).catch(() => {});
    api.get("/settings").then((r) => setCustomTpls(r.data?.notify_templates || [])).catch(() => {});
  }, []);
  const allTpls = [...TEMPLATES, ...customTpls];
  const saveTemplate = async () => {
    if (!title.trim() || !body.trim()) return toast.error("Uzupełnij tytuł i treść");
    const next = [...customTpls, { label: title.trim().slice(0, 40), kind, title, body }];
    try { await api.put("/settings", { notify_templates: next }); setCustomTpls(next); toast.success("Zapisano szablon"); }
    catch { toast.error("Błąd zapisu szablonu"); }
  };

  const multi = (e) => Array.from(e.target.selectedOptions).map((o) => o.value);
  const payload = () => ({ cities, streets, closure_ids: closureIds, splitter_ids: splitterIds, client_ids: clientIds, polygon, kind, title, body, window_from: wf, window_to: wt, channels });

  const preview = async () => {
    try {
      const { data } = await api.post("/faults/broadcast/preview", payload()); setCounts(data);
      const p = await api.post("/faults/broadcast/points", payload()); setPoints(p.data?.points || []);
      setPreviewSample(p.data?.sample || null);
      if ((p.data?.points || []).length) setShowMap(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd podglądu"); }
  };
  const send = async () => {
    if (!title.trim() || !body.trim()) return toast.error("Podaj tytuł i treść");
    if (!channels.push && !channels.email && !channels.sms) return toast.error("Wybierz kanał");
    setBusy(true);
    try {
      const { data } = await api.post("/faults/broadcast", payload());
      toast.success(`Wysyłka uruchomiona do ${data.targets?.total || 0} klientów`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd wysyłki"); }
    finally { setBusy(false); }
  };

  const winStr = wf && wt ? `${wf} → ${wt}` : (wf || wt || "");
  const _prefix = kind === "prace" ? "🔧 PRACE PLANOWE" : "⚠️ AWARIA";
  const _hasWinPh = (body + title).includes("{okno_czasowe}");
  const _win = winStr && !_hasWinPh ? `\nPlanowany czas: ${winStr}` : "";
  const _sample = previewSample || { name: "Jan Kowalski", city: "Warszawa", street: "Marszałkowska", address: "Marszałkowska 10" };
  const _fill = (txt) => fillPlaceholders(txt, _sample, winStr);
  const pvTitle = _fill(`${_prefix}: ${title || "(tytuł)"}`);
  const pvBody = _fill(`${body || "(treść)"}${_win}`);
  const pvSms = _fill(`${_prefix}: ${title || "(tytuł)"}. ${body || "(treść)"}${_win}`);

  return (
    <div className="fixed inset-0 z-[2600] bg-black/70 flex items-center justify-center p-4" data-testid="fault-broadcast-dialog">
      <div className="bg-slate-950 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 sticky top-0 bg-slate-950">
          <div className="font-bold text-slate-100 flex items-center gap-2"><Bell size={18} className="text-amber-400" /> Powiadom klientów o awarii / pracach</div>
          <button onClick={onClose} data-testid="fbd-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <label className="text-[11px] text-slate-400">Miejscowości</label>
              <select multiple value={cities} onChange={(e) => setCities(multi(e))} data-testid="fbd-cities" className={`${box} h-28`}>
                {targets.cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-400">Ulice</label>
              <select multiple value={streets} onChange={(e) => setStreets(multi(e))} data-testid="fbd-streets" className={`${box} h-28`}>
                {targets.streets.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-400">Mufy</label>
              <select multiple value={closureIds} onChange={(e) => setClosureIds(multi(e))} data-testid="fbd-closures" className={`${box} h-28`}>
                {targets.closures.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
              </select>
            </div>
          </div>
          <div data-testid="fbd-client-picker">
            <label className="text-[11px] text-slate-400">Dodaj konkretnego klienta (dowolna miejscowość)</label>
            <input value={cq} onChange={(e) => searchClients(e.target.value)} placeholder="Szukaj: nazwisko, adres, telefon…" data-testid="fbd-client-search" className={box} />
            {cResults.length > 0 && (
              <div className="mt-1 max-h-32 overflow-auto rounded border border-slate-700 bg-slate-900">
                {cResults.map((c) => (
                  <button type="button" key={c.id} data-testid={`fbd-client-opt-${c.id}`} onClick={() => addClient(c)} className="block w-full text-left px-2 py-1 text-xs text-slate-200 hover:bg-cyan-800/40">
                    {c.name || "—"} <span className="text-slate-500">· {c.address || ""} {c.city || ""}</span>
                  </button>
                ))}
              </div>
            )}
            {clientSel.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1" data-testid="fbd-client-chips">
                {clientSel.map((c) => (
                  <span key={c.id} className="text-[11px] bg-cyan-900/50 border border-cyan-700 rounded-full px-2 py-0.5 flex items-center gap-1">
                    {c.name || c.id}<button type="button" onClick={() => removeClient(c.id)} className="text-red-300">✕</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <button type="button" onClick={() => setShowMap((v) => !v)} data-testid="fbd-map-toggle" className="text-xs text-cyan-300 hover:text-cyan-200 underline flex items-center gap-1"><MapPin size={13} /> {showMap ? "Ukryj mapę" : "Zaznacz obszar na mapie"}{polygon.length > 0 ? ` (${polygon.length} pkt)` : ""}</button>
            {showMap && (
              <div className="mt-1">
                <div className="rounded-lg overflow-hidden border border-slate-700" style={{ height: 260 }} data-testid="fbd-map">
                  <MapContainer center={[52.2297, 21.0122]} zoom={12} scrollWheelZoom className="w-full h-full" style={{ background: "#0b1120" }}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" maxZoom={19} />
                    <PolyPicker polygon={polygon} setPolygon={setPolygon} />
                    {points.map((p, i) => (p.lat != null ? <CircleMarker key={`pt${i}`} center={[p.lat, p.lng]} radius={3} pathOptions={{ color: "#22d3ee", fillColor: "#22d3ee", fillOpacity: 0.9 }} /> : null))}
                  </MapContainer>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] text-slate-500">Kliknij na mapie, aby dodać wierzchołki obszaru (min. 3).</span>
                  <button type="button" onClick={() => setPolygon([])} data-testid="fbd-map-clear" className="text-[10px] text-amber-300 hover:underline">Wyczyść</button>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-4 items-center">
            <label className="flex items-center gap-1.5 text-sm text-slate-200"><input type="checkbox" checked={channels.push} onChange={(e) => setChannels({ ...channels, push: e.target.checked })} data-testid="fbd-ch-push" className="accent-cyan-500" /> <Bell size={14} /> Push</label>
            <label className="flex items-center gap-1.5 text-sm text-slate-200"><input type="checkbox" checked={channels.email} onChange={(e) => setChannels({ ...channels, email: e.target.checked })} data-testid="fbd-ch-email" className="accent-cyan-500" /> <Mail size={14} /> E-mail</label>
            <label className="flex items-center gap-1.5 text-sm text-slate-200"><input type="checkbox" checked={channels.sms} onChange={(e) => setChannels({ ...channels, sms: e.target.checked })} data-testid="fbd-ch-sms" className="accent-cyan-500" /> <MessageSquare size={14} /> SMS</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="fbd-kind" className={`${box} w-auto`}>
              <option value="awaria">⚠️ Awaria</option>
              <option value="prace">🔧 Prace planowe</option>
            </select>
          </div>

          <div className="flex gap-2">
            <select data-testid="fbd-template" onChange={(e) => { const tpl = allTpls[+e.target.value]; if (tpl && tpl.title) { setKind(tpl.kind); setTitle(tpl.title); setBody(tpl.body); } }} className={box}>
              {allTpls.map((tpl, i) => <option key={i} value={i}>{tpl.label}</option>)}
            </select>
            <button type="button" onClick={saveTemplate} data-testid="fbd-save-tpl" className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2 whitespace-nowrap text-slate-200">Zapisz szablon</button>
          </div>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Tytuł (np. Przerwa w dostępie)" data-testid="fbd-title" className={box} />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Treść powiadomienia dla klientów…" data-testid="fbd-body" rows={3} className={box} />
          <p className="text-[10px] text-slate-500" data-testid="fbd-placeholder-hint">
            Znaczniki (podstawiane per klient przy wysyłce): <code className="text-cyan-300">{"{imie}"}</code> <code className="text-cyan-300">{"{miasto}"}</code> <code className="text-cyan-300">{"{ulica}"}</code> <code className="text-cyan-300">{"{adres}"}</code> <code className="text-cyan-300">{"{okno_czasowe}"}</code>
          </p>
          <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-2.5" data-testid="fbd-live-preview">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Podgląd wiadomości · dla: <span className="text-slate-300">{_sample.name || "—"}</span>{previewSample ? "" : " (przykład)"}</div>
            <div className="text-[12px] text-slate-100 font-semibold whitespace-pre-wrap" data-testid="fbd-preview-title">{pvTitle}</div>
            <div className="text-[12px] text-slate-300 whitespace-pre-wrap" data-testid="fbd-preview-body">{pvBody}</div>
            <div className="mt-2 pt-2 border-t border-slate-800 text-[11px] text-slate-400"><span className="text-cyan-300">SMS:</span> <span className="whitespace-pre-wrap" data-testid="fbd-preview-sms">{pvSms}</span></div>
          </div>
          {kind === "prace" && (
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[11px] text-slate-400">Okno: od</label><input type="datetime-local" value={wf} onChange={(e) => setWf(e.target.value)} data-testid="fbd-wf" className={box} /></div>
              <div><label className="text-[11px] text-slate-400">Okno: do</label><input type="datetime-local" value={wt} onChange={(e) => setWt(e.target.value)} data-testid="fbd-wt" className={box} /></div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button onClick={preview} data-testid="fbd-preview" className="text-sm bg-slate-700 hover:bg-slate-600 rounded px-3 py-1.5 flex items-center gap-1.5"><Users size={14} /> Podgląd odbiorców</button>
            {counts && (
              <div className="text-[12px] text-slate-300" data-testid="fbd-counts">
                Razem: <b className="text-cyan-300">{counts.total}</b> · Push: <b>{counts.with_push}</b> · E-mail: <b>{counts.with_email}</b> · SMS: <b>{counts.with_sms}</b>
                {counts.clients && counts.clients.length > 0 && (
                  <div className="mt-1.5 max-h-40 overflow-auto rounded border border-slate-700 bg-slate-900/60 divide-y divide-slate-800" data-testid="fbd-recipients">
                    {counts.clients.map((c) => (
                      <div key={c.id} className="px-2 py-1 flex items-center justify-between gap-2" data-testid={`fbd-recipient-${c.id}`}>
                        <span className="text-slate-200 truncate">{c.name || "—"} <span className="text-slate-500">· {c.address} {c.city}</span></span>
                        <span className="text-[10px] text-slate-500 shrink-0">{c.phone ? "SMS" : ""}{c.email ? " ·@" : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-800 sticky bottom-0 bg-slate-950">
          <button onClick={send} disabled={busy} data-testid="fbd-send" className="w-full bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white rounded py-2 text-sm font-semibold flex items-center justify-center gap-2">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Wyślij powiadomienie
          </button>
        </div>
      </div>
    </div>
  );
}
